import { useState, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { MediaUri, productStatusOptions } from "../../../constants/enum";

// Types
export interface Product {
  id: string;
  brand: string;
  brandType: string;
  materialCode: string;
  materialName?: string;
  barcode: string;
  name: string;
  titleEN: string;
  titleTH: string;
  category: string;
  subcategory?: string;
  collection?: string;
  color?: string;
  packaging?: string;
  status?: string;
  description?: string;
  descriptionEN?: string;
  descriptionTH?: string;
  images?: string;
  productHierachy?: string;
  [key: string]: any;
}

interface SerieGroup {
  name: string;
  count: number;
  products: Product[];
}

interface SerieGroupCluster {
  key: string;
  series: Record<string, SerieGroup>;
  totalCount: number;
}

interface HierarchyNode {
  name: string;
  count: number;
  children?: Record<string, HierarchyNode>;
  products?: Product[];
  series?: Record<string, SerieGroup>;
}

interface FolderViewProps {
  products: Product[];
  themeColors: { bg: string; border: string; text: string };
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

// Helper function to get serie key from productHierachy (first 13 characters)
const getSerieKey = (productHierachy: string | undefined): string => {
  if (!productHierachy || productHierachy.length < 13) return "Unknown Serie";
  return productHierachy.substring(0, 13);
};

// Helper function to extract product name prefix (chars before first number)
const getProductNamePrefix = (name: string | undefined): string => {
  if (!name) return "Unknown";
  const match = name.match(/^([^\d]+)/);
  return match ? match[1].trim() : "Unknown";
};

// Helper function to get last character of serie key
const getSerieGroupKey = (serieKey: string): string => {
  if (serieKey.length < 2) return serieKey;
  return serieKey.substring(serieKey.length - 1);
};

// Helper function to group series by last char + product name prefix
const groupSeriesByClusters = (series: Record<string, SerieGroup>): SerieGroupCluster[] => {
  const clusterMap: Record<string, SerieGroupCluster> = {};

  Object.entries(series).forEach(([serieKey, serieGroup]) => {
    const last2Chars = getSerieGroupKey(serieKey);
    const firstProduct = serieGroup.products[0];
    const namePrefix = getProductNamePrefix(firstProduct?.name);
    const clusterKey = `${last2Chars}|${namePrefix}`;

    if (!clusterMap[clusterKey]) {
      clusterMap[clusterKey] = {
        key: clusterKey,
        series: {},
        totalCount: 0,
      };
    }

    clusterMap[clusterKey].series[serieKey] = serieGroup;
    clusterMap[clusterKey].totalCount += serieGroup.count;
  });

  return Object.values(clusterMap).sort((a, b) => a.key.localeCompare(b.key));
};

// Helper function to build hierarchy from flat array
const buildHierarchy = (products: Product[]): Record<string, HierarchyNode> => {
  const hierarchy: Record<string, HierarchyNode> = {};

  products.forEach((product) => {
    const category = product.category || "Uncategorized";
    const subcategory = product.subcategory || "General";
    const brandType = product.brandType || "Standard";
    const serieKey = getSerieKey(product.productHierachy);

    if (!hierarchy[category]) {
      hierarchy[category] = { name: category, count: 0, children: {} };
    }
    hierarchy[category].count++;

    if (!hierarchy[category].children![subcategory]) {
      hierarchy[category].children![subcategory] = { name: subcategory, count: 0, children: {} };
    }
    hierarchy[category].children![subcategory].count++;

    if (!hierarchy[category].children![subcategory].children![brandType]) {
      hierarchy[category].children![subcategory].children![brandType] = {
        name: brandType,
        count: 0,
        series: {},
      };
    }
    hierarchy[category].children![subcategory].children![brandType].count++;

    if (!hierarchy[category].children![subcategory].children![brandType].series![serieKey]) {
      hierarchy[category].children![subcategory].children![brandType].series![serieKey] = {
        name: serieKey,
        count: 0,
        products: [],
      };
    }
    hierarchy[category].children![subcategory].children![brandType].series![serieKey].count++;
    hierarchy[category].children![subcategory].children![brandType].series![serieKey].products.push(product);
  });

  return hierarchy;
};

// Helper function to get status label from productStatusOptions
const getStatusLabel = (statusValue: string | undefined): string | null => {
  if (!statusValue) return null;
  const status = productStatusOptions.find((opt) => opt.value === statusValue);
  return status ? status.label : null;
};

// Status badge color mapping
const statusColors: Record<string, { bg: string; text: string }> = {
  "01": { bg: "#DBEAFE", text: "#1E40AF" },
  "02": { bg: "#D1FAE5", text: "#065F46" },
  "03": { bg: "#FEF3C7", text: "#92400E" },
  "04": { bg: "#E5E7EB", text: "#374151" },
  "05": { bg: "#FEE2E2", text: "#991B1B" },
  "06": { bg: "#F3E8FF", text: "#6B21A8" },
};

// Product Card Component
const ProductCard = ({ product, themeColors }: { product: Product; themeColors: { bg: string; border: string; text: string } }) => {
  const images = product.images ? JSON.parse(product.images) : null;
  const firstImage = Array.isArray(images) && images.length > 0 ? images[0] : null;
  const imageUrl = firstImage ? `${MediaUri}/display/${firstImage}` : null;
  const [imageError, setImageError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [tooltipPlacement, setTooltipPlacement] = useState<"top" | "bottom" | "left" | "right">("top");
  const cardRef = useRef<HTMLDivElement>(null);

  const statusLabel = getStatusLabel(product.status);
  const statusColor = product.status ? statusColors[product.status] : null;

  const handleClick = () => {
    const productUrl = `/product/${product.barcode}_${product.materialCode}/show`;
    window.open(productUrl, "_blank");
  };

  const handleMouseEnter = () => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const tooltipWidth = 256; // w-64 = 16rem = 256px
      const tooltipHeight = 200; // estimated height
      const margin = 4; // reduced margin for closer positioning

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top: number;
      let left: number;

      // Center tooltip horizontally relative to card
      left = rect.left + (rect.width / 2) - (tooltipWidth / 2);

      // Ensure tooltip doesn't go off screen horizontally
      if (left < margin) {
        left = margin;
      } else if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }

      // Priority: Top first, then Bottom, then Right, then Left
      let placement: "top" | "bottom" | "left" | "right" = "top";

      if (rect.top - margin - tooltipHeight > 0) {
        // Place above
        top = (rect.top - tooltipHeight - margin) * 1.1;
        placement = "top";
      } else if (rect.bottom + margin + tooltipHeight < viewportHeight) {
        // Place below
        top = rect.bottom + margin;
        placement = "bottom";
      } else if (rect.right + margin + tooltipWidth < viewportWidth) {
        // Place right
        top = rect.top;
        left = rect.right + margin;
        placement = "right";
      } else if (rect.left - margin - tooltipWidth > 0) {
        // Place left
        top = rect.top;
        left = rect.left - margin - tooltipWidth;
        placement = "left";
      } else {
        // Default: place above and adjust
        top = margin;
        placement = "top";
      }

      setTooltipPosition({ top, left });
      setTooltipPlacement(placement);
    }
    setShowTooltip(true);
  };

  // Check if there's any content to show in tooltip
  const hasTooltipContent = product.titleEN || product.titleTH || product.description || product.descriptionEN || product.descriptionTH;

  return (
    <div
      ref={cardRef}
      className="relative flex flex-col items-center p-2 transition-shadow bg-white border border-gray-100 rounded-lg shadow-sm cursor-pointer hover:shadow-md"
      style={{ width: "127px" }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Tooltip using Portal */}
      {showTooltip && hasTooltipContent && createPortal(
        <div
          className="fixed z-[9999] w-64 p-3 text-left bg-gray-900 rounded-lg shadow-xl text-white text-[11px] leading-relaxed"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          {/* Arrow - position based on tooltip placement */}
          {tooltipPlacement === "top" && (
            <div className="absolute w-2 h-2 rotate-45 -translate-x-1/2 bg-gray-900 left-1/2 -bottom-1" />
          )}
          {tooltipPlacement === "bottom" && (
            <div className="absolute w-2 h-2 rotate-45 -translate-x-1/2 bg-gray-900 left-1/2 -top-1" />
          )}
          {tooltipPlacement === "right" && (
            <div className="absolute w-2 h-2 rotate-45 bg-gray-900 -left-1 top-4" />
          )}
          {tooltipPlacement === "left" && (
            <div className="absolute w-2 h-2 rotate-45 bg-gray-900 -right-1 top-4" />
          )}

          {product.titleEN && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Title EN:</span>
              <p className="mt-0.5">{product.titleEN}</p>
            </div>
          )}
          {product.titleTH && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Title TH:</span>
              <p className="mt-0.5">{product.titleTH}</p>
            </div>
          )}
          {product.description && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Description:</span>
              <p className="mt-0.5">{product.description}</p>
            </div>
          )}
          {product.descriptionEN && (
            <div className="mb-2">
              <span className="font-semibold text-gray-300">Desc EN:</span>
              <p className="mt-0.5">{product.descriptionEN}</p>
            </div>
          )}
          {product.descriptionTH && (
            <div>
              <span className="font-semibold text-gray-300">Desc TH:</span>
              <p className="mt-0.5">{product.descriptionTH}</p>
            </div>
          )}
        </div>,
        document.body
      )}

      {statusLabel && statusColor && (
        <div
          className="absolute top-1 right-1 px-1.5 py-0.5 text-[10px] font-medium rounded"
          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
        >
          {statusLabel}
        </div>
      )}
      <div className="flex items-center justify-center w-full h-20 mb-2 rounded bg-gray-50">
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={product.name || "Product"}
            className="object-contain w-full h-full"
            loading="lazy"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full text-xs text-gray-400">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>
      <div className="flex flex-col justify-between flex-1 w-full text-center">
        <p className="h-8 text-xs font-medium leading-tight text-gray-700 line-clamp-2">
          {product.name || product.materialName || "N/A"}
        </p>
        <p
          className="mt-1 text-xs font-mono px-1 py-0.5 rounded"
          style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
        >
          {product.materialCode}
        </p>
      </div>
    </div>
  );
};

// Accordion Item Component
const AccordionItem = ({
  title,
  count,
  level,
  isExpanded,
  onToggle,
  themeColors,
  children,
}: {
  title: string;
  count: number;
  level: number;
  isExpanded: boolean;
  onToggle: () => void;
  themeColors: { bg: string; border: string; text: string };
  children: React.ReactNode;
}) => {
  const paddingLeft = level * 20;

  return (
    <div className={`border-b border-gray-100 last:border-b-0 ${level === 0 ? "" : ""}`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between py-3 px-4 text-left transition-colors ${
          level === 0 ? "hover:bg-gray-50" : "hover:bg-gray-50/50"
        }`}
        style={{ paddingLeft: paddingLeft + 16 }}
      >
        <div className="flex items-center gap-3">
          <svg
            className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isExpanded ? "rotate-90" : ""}`}
            style={{ color: themeColors.border }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span
            className={`font-medium ${level === 0 ? "text-base" : "text-sm"}`}
            style={{ color: level === 0 ? themeColors.text : "#374151" }}
          >
            {title}
          </span>
        </div>
        <span
          className={`px-2.5 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${
            level === 0 ? "" : level === 1 ? "opacity-80" : "opacity-60"
          }`}
          style={{
            backgroundColor: themeColors.bg,
            color: themeColors.text,
          }}
        >
          {count.toLocaleString()}
        </span>
      </button>
      {isExpanded && (
        <div className="pb-2">{children}</div>
      )}
    </div>
  );
};

function FolderView({ products, themeColors, searchTerm, setSearchTerm }: FolderViewProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Build hierarchy from products
  const hierarchy = useMemo(() => buildHierarchy(products), [products]);

  // Toggle accordion node
  const toggleNode = useCallback((nodeKey: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeKey)) {
        next.delete(nodeKey);
      } else {
        next.add(nodeKey);
      }
      return next;
    });
  }, []);

  // Expand all categories
  const expandAll = useCallback(() => {
    const allKeys = new Set<string>();
    Object.entries(hierarchy).forEach(([catKey, catNode]) => {
      allKeys.add(catKey);
      if (catNode.children) {
        Object.entries(catNode.children).forEach(([subKey, subNode]) => {
          allKeys.add(`${catKey}|${subKey}`);
          if (subNode.children) {
            Object.keys(subNode.children).forEach((btKey) => {
              allKeys.add(`${catKey}|${subKey}|${btKey}`);
            });
          }
        });
      }
    });
    setExpandedNodes(allKeys);
  }, [hierarchy]);

  // Collapse all
  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  // Summary stats
  const stats = useMemo(() => {
    const categories = Object.keys(hierarchy).length;
    let subcategories = 0;
    let brandTypes = 0;
    let series = 0;

    Object.values(hierarchy).forEach((cat) => {
      if (cat.children) {
        subcategories += Object.keys(cat.children).length;
        Object.values(cat.children).forEach((sub) => {
          if (sub.children) {
            brandTypes += Object.keys(sub.children).length;
            Object.values(sub.children).forEach((bt) => {
              if (bt.series) {
                series += Object.keys(bt.series).length;
              }
            });
          }
        });
      }
    });

    return { categories, subcategories, brandTypes, series, products: products.length };
  }, [hierarchy, products]);

  return (
    <div className="mt-4">
      {/* Search and Stats Bar */}
      <div className="p-4 mb-4 bg-white rounded-lg shadow">
        <div className="flex flex-col gap-4">
          {/* Top row: Search + Buttons */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            {/* Search Input */}
            <div className="relative flex-1">
              <svg
                className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by name, material code, barcode, category, subcategory..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2 pl-10 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": themeColors.border } as React.CSSProperties}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Expand/Collapse Buttons */}
            <div className="flex flex-shrink-0 gap-2">
              <button
                onClick={expandAll}
                className="px-3 py-2 text-sm transition-colors border rounded-lg hover:bg-gray-50 whitespace-nowrap"
                style={{ borderColor: themeColors.border, color: themeColors.text }}
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-2 text-sm text-gray-600 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Bottom row: Stats */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="px-2 py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.categories}</strong> Categories
            </span>
            <span className="px-2 py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.subcategories}</strong> Sub-categories
            </span>
            <span className="px-2 py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.brandTypes}</strong> Brand Types
            </span>
            <span className="px-2 py-1 text-gray-600 bg-gray-100 rounded">
              <strong>{stats.series}</strong> Series
            </span>
            <span
              className="px-2 py-1 font-medium rounded"
              style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
            >
              <strong>{stats.products.toLocaleString()}</strong> Products
            </span>
          </div>
        </div>
      </div>

      {/* Hierarchy Accordion */}
      <div className="overflow-hidden bg-white rounded-lg shadow">
        {Object.entries(hierarchy)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([categoryKey, categoryNode]) => (
            <AccordionItem
              key={categoryKey}
              title={categoryKey}
              count={categoryNode.count}
              level={0}
              isExpanded={expandedNodes.has(categoryKey)}
              onToggle={() => toggleNode(categoryKey)}
              themeColors={themeColors}
            >
              {categoryNode.children &&
                Object.entries(categoryNode.children)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([subKey, subNode]) => {
                    const subNodeKey = `${categoryKey}|${subKey}`;
                    return (
                      <AccordionItem
                        key={subNodeKey}
                        title={subKey}
                        count={subNode.count}
                        level={1}
                        isExpanded={expandedNodes.has(subNodeKey)}
                        onToggle={() => toggleNode(subNodeKey)}
                        themeColors={themeColors}
                      >
                        {subNode.children &&
                          Object.entries(subNode.children)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([btKey, btNode]) => {
                              const btNodeKey = `${categoryKey}|${subKey}|${btKey}`;
                              return (
                                <AccordionItem
                                  key={btNodeKey}
                                  title={btKey}
                                  count={btNode.count}
                                  level={2}
                                  isExpanded={expandedNodes.has(btNodeKey)}
                                  onToggle={() => toggleNode(btNodeKey)}
                                  themeColors={themeColors}
                                >
                                  {/* Series grouped by clusters - inline display */}
                                  {btNode.series && (
                                    <div className="px-4 py-4 ml-12 md:ml-16">
                                      <div className="flex flex-wrap items-start gap-3">
                                        {groupSeriesByClusters(btNode.series).map((cluster) => (
                                          <div
                                            key={cluster.key}
                                            className="inline-flex flex-col p-3 border-2 border-solid rounded-xl"
                                            style={{ borderColor: themeColors.text, backgroundColor: `${themeColors.bg}30` }}
                                          >
                                            {/* Cluster Header */}
                                            <div className="flex items-center gap-2 pb-2 mb-2 border-b" style={{ borderColor: themeColors.border }}>
                                              <span
                                                className="px-2 py-0.5 text-[10px] font-bold rounded"
                                                style={{ backgroundColor: themeColors.text, color: "white" }}
                                              >
                                                {cluster.key.split("|")[1] || "Unknown"}
                                              </span>
                                              <span className="text-[10px] text-gray-600">
                                                ({cluster.totalCount})
                                              </span>
                                            </div>
                                            {/* Series inside cluster - inline */}
                                            <div className="flex flex-wrap items-start gap-2">
                                              {Object.entries(cluster.series)
                                                .sort(([a], [b]) => a.localeCompare(b))
                                                .map(([serieKey, serieGroup]) => (
                                                  <div
                                                    key={serieKey}
                                                    className="inline-flex flex-col p-2 bg-white border-2 border-dashed rounded-lg"
                                                    style={{ borderColor: themeColors.border }}
                                                  >
                                                    {/* Serie Header */}
                                                    <div className="flex items-center gap-1 mb-1">
                                                      <span
                                                        className="px-1 py-0.5 text-[9px] font-semibold rounded"
                                                        style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
                                                      >
                                                        {serieKey}
                                                      </span>
                                                      <span className="text-[9px] text-gray-500">
                                                        ({serieGroup.count})
                                                      </span>
                                                    </div>
                                                    {/* Products in serie - inline */}
                                                    <div className="flex flex-wrap gap-1">
                                                      {serieGroup.products.map((product, idx) => (
                                                        <ProductCard
                                                          key={product.id || idx}
                                                          product={product}
                                                          themeColors={themeColors}
                                                        />
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </AccordionItem>
                              );
                            })}
                      </AccordionItem>
                    );
                  })}
            </AccordionItem>
          ))}
      </div>

      {/* No Results */}
      {Object.keys(hierarchy).length === 0 && searchTerm && (
        <div className="py-12 text-center bg-white rounded-lg shadow">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-lg text-gray-500">No products found for "{searchTerm}"</p>
          <button
            onClick={() => setSearchTerm("")}
            className="px-4 py-2 mt-4 text-sm transition-colors rounded-lg"
            style={{ backgroundColor: themeColors.bg, color: themeColors.text }}
          >
            Clear search
          </button>
        </div>
      )}
    </div>
  );
}

export default FolderView;
